import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Users,
  UserCheck,
  UserX,
  Clock,
  DollarSign,
  Calendar,
  Upload,
  Building2,
  ArrowRight,
  UserPlus,
  BarChart3,
  ShieldCheck,
  Briefcase,
  KeyRound,
  Globe,
  Network,
  Settings,
  type LucideIcon,
} from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { useDashboardStats, useQuickActions, useRecentActivity } from '@/hooks/useDashboard';
import { useDepartments } from '@/hooks/useDepartments';
import { useManagedEmployees } from '@/features/hrms/leaves/hooks/useLeaves';
import { OnboardingChecklist } from '@/components/OnboardingChecklist';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { useLocaleFormat } from '@/hooks/useLocaleFormat';
import { useTenant, useUser } from '@/stores';
import { cn } from '@/lib/utils';
import {
  buildAccessContext,
  buildDashboardStats,
  filterVisibleQuickActions,
  getDashboardAudience,
  getDashboardSelfWorkspace,
  getVisibleOrganizationSetupSections,
} from './dashboard-view-model';

const QUICK_ACTION_ICONS: Record<string, LucideIcon> = {
  UserPlus,
  Calendar,
  Clock,
  DollarSign,
  BarChart3,
  Building2,
};

const ORGANIZATION_SETUP_LINKS = [
  {
    key: 'profile',
    title: 'Company Profile',
    description: 'Name, email, logo, and contact details',
    href: '/settings/organization/profile',
    icon: Building2,
  },
  {
    key: 'regional',
    title: 'Regional Settings',
    description: 'Timezone, currency, locale, and working days',
    href: '/settings/organization/regional',
    icon: Globe,
  },
  {
    key: 'departments',
    title: 'Departments',
    description: 'Department hierarchy and heads',
    href: '/settings/organization/departments',
    icon: Network,
  },
  {
    key: 'teams',
    title: 'Teams',
    description: 'Team membership and leads',
    href: '/settings/organization/teams',
    icon: Users,
  },
] as const;

// Safe percentage calculation to avoid NaN
function safePercentage(value: number, total: number, decimals = 1): string {
  if (total === 0) return '0';
  return ((value / total) * 100).toFixed(decimals);
}

export function DashboardPage() {
  const user = useUser();
  const tenant = useTenant();
  const permissions = usePermissions();
  const { formatCurrency, formatDateTime } = useLocaleFormat();
  const dashboardAudience = getDashboardAudience(permissions.hasPermission);
  const canViewTenantDashboard = dashboardAudience === 'tenant';
  const canViewManagedDashboard = dashboardAudience === 'managed';
  const canViewDepartments = permissions.hasPermission('hrms:department:read:tenant');
  const selfWorkspace = getDashboardSelfWorkspace(user, permissions.hasPermission);

  // Fetch tenant-wide dashboard data only for roles allowed to view tenant/team dashboard metrics.
  const { data: dashboardStats, isLoading: statsLoading } = useDashboardStats({
    enabled: canViewTenantDashboard,
  });
  const { data: recentActivityData, isLoading: activityLoading } = useRecentActivity(5, {
    enabled: canViewTenantDashboard,
  });
  const { data: quickActionsData, isLoading: quickActionsLoading } = useQuickActions({
    enabled: canViewTenantDashboard,
  });
  const { data: departmentsData, isLoading: departmentsLoading } = useDepartments(undefined, {
    enabled: canViewTenantDashboard && canViewDepartments,
  });
  const {
    data: managedEmployees = [],
    isLoading: managedEmployeesLoading,
    isError: managedEmployeesError,
    refetch: refetchManagedEmployees,
  } = useManagedEmployees(canViewManagedDashboard);

  const stats = buildDashboardStats(dashboardStats);
  const recentActivities = recentActivityData?.activities ?? [];
  const visibleQuickActions = filterVisibleQuickActions(
    quickActionsData ?? [],
    permissions.hasPermission,
  );
  const accessContext = buildAccessContext(
    user ? { ...user, tenantName: tenant?.name ?? user.tenantName } : null,
  );
  const visibleOrganizationSetupSections = new Set(
    getVisibleOrganizationSetupSections(permissions.hasPermission),
  );
  const visibleOrganizationSetupLinks = ORGANIZATION_SETUP_LINKS.filter((item) =>
    visibleOrganizationSetupSections.has(item.key),
  );
  const canViewOrganizationSetup = visibleOrganizationSetupLinks.length > 0;
  const managedDepartmentCount = new Set(
    managedEmployees
      .map((employee) => employee.department?.id)
      .filter((departmentId): departmentId is string => Boolean(departmentId)),
  ).size;
  const managedDesignationCount = new Set(
    managedEmployees
      .map((employee) => employee.designation?.id)
      .filter((designationId): designationId is string => Boolean(designationId)),
  ).size;
  const employmentStatusItems = [
    {
      label: 'Active employees',
      value: stats.activeEmployees,
      percent: safePercentage(stats.activeEmployees, stats.totalEmployees, 0),
      Icon: UserCheck,
      tone: 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900/70',
      dot: 'bg-emerald-500',
    },
    {
      label: 'On leave',
      value: stats.onLeave,
      percent: safePercentage(stats.onLeave, stats.totalEmployees, 0),
      Icon: Clock,
      tone: 'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900/70',
      dot: 'bg-amber-500',
    },
    {
      label: 'Inactive',
      value: stats.inactive,
      percent: safePercentage(stats.inactive, stats.totalEmployees, 0),
      Icon: UserX,
      tone: 'bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-900/70 dark:text-slate-300 dark:ring-slate-800',
      dot: 'bg-slate-400',
    },
  ] as const;

  // Build department stats from real data
  const departmentStats = (departmentsData?.data ?? []).slice(0, 6).map((dept) => ({
    name: dept.name,
    count: (dept as { employeeCount?: number }).employeeCount ?? 0,
    percentage: (dept as { attendanceRate?: number }).attendanceRate ?? 0,
  }));

  return (
    <div className="flex-1 space-y-4">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Dashboard</h2>
          <p className="text-sm text-muted-foreground">
            {canViewTenantDashboard
              ? "Welcome back. Here's what's happening across your organization."
              : canViewManagedDashboard
                ? 'Welcome back. Review your management scope and team work.'
                : selfWorkspace.kind === 'governance'
                  ? 'Welcome back. Review attendance decisions explicitly assigned to you.'
                  : 'Welcome back. Review your work context and open an available task.'}
          </p>
        </div>
      </div>

      {/* Onboarding Checklist */}
      <OnboardingChecklist enabled={canViewTenantDashboard} />

      {/* Tenant and user access context */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-7">
        <Card
          className={cn(
            'min-w-0 p-6',
            canViewOrganizationSetup ? 'lg:col-span-4' : 'lg:col-span-7',
          )}
        >
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h3 className="text-lg font-semibold">My Work Context</h3>
              <p className="text-sm text-muted-foreground">
                Role, HR placement, and permission scope from organization setup
              </p>
            </div>
            <Badge
              variant="outline"
              className="w-fit shrink-0 gap-1.5 border-sky-200 bg-sky-50 px-2.5 py-1 font-medium text-sky-900 dark:border-sky-900/60 dark:bg-sky-950/40 dark:text-sky-200"
            >
              <ShieldCheck className="h-3.5 w-3.5" />
              {accessContext.scopeLabel}
            </Badge>
          </div>
          <dl className="grid grid-cols-1 border-y sm:grid-cols-2">
            <div className="border-b py-3 sm:border-r sm:pr-4">
              <div className="mb-2 flex items-center gap-2 text-xs font-medium uppercase text-muted-foreground">
                <ShieldCheck className="h-3.5 w-3.5" />
                <dt>Role</dt>
              </div>
              <dd className="break-words font-medium">{accessContext.roleLabel}</dd>
            </div>
            <div className="border-b py-3 sm:pl-4">
              <div className="mb-2 flex items-center gap-2 text-xs font-medium uppercase text-muted-foreground">
                <Briefcase className="h-3.5 w-3.5" />
                <dt>Position</dt>
              </div>
              <dd className="break-words font-medium">{accessContext.positionLabel}</dd>
            </div>
            <div className="border-b py-3 sm:border-b-0 sm:border-r sm:pr-4">
              <div className="mb-2 flex items-center gap-2 text-xs font-medium uppercase text-muted-foreground">
                <Building2 className="h-3.5 w-3.5" />
                <dt>Home Department</dt>
              </div>
              <dd className="break-words font-medium">{accessContext.departmentLabel}</dd>
            </div>
            <div className="py-3 sm:pl-4">
              <div className="mb-2 flex items-center gap-2 text-xs font-medium uppercase text-muted-foreground">
                <KeyRound className="h-3.5 w-3.5" />
                <dt>Permissions</dt>
              </div>
              <dd className="break-words font-medium">{accessContext.permissionSummary.label}</dd>
            </div>
          </dl>
          <p className="mt-3 text-sm text-muted-foreground">{accessContext.scopeDescription}</p>
          {accessContext.permissionSummary.shouldList && (
            <div className="mt-3 flex flex-wrap gap-2">
              {accessContext.permissionSummary.preview.map((permission) => (
                <Badge key={permission} variant="outline" className="max-w-full truncate">
                  {permission}
                </Badge>
              ))}
            </div>
          )}
        </Card>

        {canViewOrganizationSetup && (
          <Card className="min-w-0 p-6 lg:col-span-3">
            <div className="mb-4">
              <h3 className="text-lg font-semibold">Organization Setup</h3>
              <p className="text-sm text-muted-foreground">
                Manage {tenant?.name ?? user?.tenantName ?? 'the current organization'}
              </p>
            </div>
            <div className="space-y-2">
              {visibleOrganizationSetupLinks.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className="flex items-center gap-3 rounded-lg border p-3 transition-colors hover:bg-muted/50"
                >
                  <div className="rounded-md bg-primary/10 p-2">
                    <item.icon className="h-4 w-4 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{item.title}</p>
                    <p className="truncate text-xs text-muted-foreground">{item.description}</p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </Link>
              ))}
            </div>
          </Card>
        )}
      </div>

      {/* Stats Grid */}
      {canViewTenantDashboard ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card className="min-w-0 p-6">
            <div className="flex items-center justify-between space-y-0 pb-2">
              <p className="text-sm font-medium text-muted-foreground">Total Employees</p>
              <Users className="h-4 w-4 text-muted-foreground" />
            </div>
            {statsLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-8 w-20" />
                <Skeleton className="h-4 w-32" />
              </div>
            ) : (
              <div className="space-y-1">
                <p className="text-2xl font-bold">{stats.totalEmployees}</p>
                <p className="text-xs text-muted-foreground">
                  <span className="text-green-600">+{stats.newHires}</span> new this month
                </p>
              </div>
            )}
          </Card>

          <Card className="min-w-0 p-6">
            <div className="flex items-center justify-between space-y-0 pb-2">
              <p className="text-sm font-medium text-muted-foreground">Active Employees</p>
              <UserCheck className="h-4 w-4 text-muted-foreground" />
            </div>
            {statsLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-8 w-20" />
                <Skeleton className="h-4 w-32" />
              </div>
            ) : (
              <div className="space-y-1">
                <p className="text-2xl font-bold">{stats.activeEmployees}</p>
                <p className="text-xs text-muted-foreground">
                  {safePercentage(stats.activeEmployees, stats.totalEmployees)}% of total
                </p>
              </div>
            )}
          </Card>

          <Card className="min-w-0 p-6">
            <div className="flex items-center justify-between space-y-0 pb-2">
              <p className="text-sm font-medium text-muted-foreground">Monthly Payroll</p>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </div>
            {statsLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-8 w-20" />
                <Skeleton className="h-4 w-32" />
              </div>
            ) : (
              <div className="space-y-1">
                <p className="text-2xl font-bold">{formatCurrency(stats.monthlyPayroll)}</p>
                <p className="text-xs text-muted-foreground">
                  Avg {formatCurrency(stats.avgSalary)} per employee
                </p>
              </div>
            )}
          </Card>

          <Card className="min-w-0 p-6">
            <div className="flex items-center justify-between space-y-0 pb-2">
              <p className="text-sm font-medium text-muted-foreground">Pending Leave Requests</p>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </div>
            {statsLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-8 w-20" />
                <Skeleton className="h-4 w-32" />
              </div>
            ) : (
              <div className="space-y-1">
                <p className="text-2xl font-bold">{stats.pendingLeaves}</p>
                <p className="text-xs text-muted-foreground">
                  {stats.hasReportees === false
                    ? 'No reportees assigned'
                    : stats.pendingLeaves > 0
                      ? 'Requires attention'
                      : 'All caught up'}
                </p>
              </div>
            )}
          </Card>
        </div>
      ) : canViewManagedDashboard ? (
        <Card className="min-w-0 p-5 sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-lg font-semibold">Manager Workspace</h3>
                <Badge variant="outline">Scoped people</Badge>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                {accessContext.scopeDescription} Tenant-wide workforce data remains hidden.
              </p>
            </div>
            <Button asChild className="shrink-0">
              <Link to="/employees/my-team">
                Open My Team
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>

          {managedEmployeesLoading ? (
            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {Array.from({ length: 3 }).map((_, index) => (
                <Skeleton key={index} className="h-20 w-full rounded-lg" />
              ))}
            </div>
          ) : managedEmployeesError ? (
            <div className="mt-5 rounded-lg border border-destructive/40 p-4">
              <p className="font-medium">Your management scope could not be loaded</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Retry this scoped request. Tenant-wide statistics remain hidden.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => refetchManagedEmployees()}
              >
                Retry
              </Button>
            </div>
          ) : (
            <div className="mt-5 grid grid-cols-1 divide-y rounded-lg border sm:grid-cols-3 sm:divide-x sm:divide-y-0">
              <div className="px-4 py-3">
                <p className="text-xs font-medium uppercase text-muted-foreground">People</p>
                <p className="mt-1 text-2xl font-semibold tabular-nums">
                  {managedEmployees.length}
                </p>
              </div>
              <div className="px-4 py-3">
                <p className="text-xs font-medium uppercase text-muted-foreground">Departments</p>
                <p className="mt-1 text-2xl font-semibold tabular-nums">{managedDepartmentCount}</p>
              </div>
              <div className="px-4 py-3">
                <p className="text-xs font-medium uppercase text-muted-foreground">Designations</p>
                <p className="mt-1 text-2xl font-semibold tabular-nums">
                  {managedDesignationCount}
                </p>
              </div>
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            {permissions.hasAnyPermission([
              'attendance:timesheet:read:team',
              'attendance:timesheet:read:department',
            ]) && (
              <Button asChild variant="outline" size="sm">
                <Link to="/attendance/timesheet">Team Timesheets</Link>
              </Button>
            )}
            {permissions.hasAnyPermission([
              'attendance:timesheet:approve:team',
              'attendance:timesheet:approve:department',
            ]) && (
              <Button asChild variant="outline" size="sm">
                <Link to="/attendance/timesheet/approvals">Timesheet Approvals</Link>
              </Button>
            )}
            {permissions.hasAnyPermission([
              'hrms:leave-request:approve:team',
              'hrms:leave-request:approve:department',
            ]) && (
              <Button asChild variant="outline" size="sm">
                <Link to="/leaves/requests">Leave Requests</Link>
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <Card className="min-w-0 p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-lg font-semibold">{selfWorkspace.title}</h3>
              <p className="text-sm text-muted-foreground">{selfWorkspace.description}</p>
            </div>
            <Button asChild>
              <Link to={selfWorkspace.href}>
                {selfWorkspace.actionLabel}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </Card>
      )}

      {canViewTenantDashboard && (
        <>
          {/* Two Column Layout */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-7">
            {/* Department Statistics */}
            {canViewDepartments && (
              <Card className="min-w-0 p-6 lg:col-span-4">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold">Department Overview</h3>
                  <p className="text-sm text-muted-foreground">
                    Employee distribution and attendance rates
                  </p>
                </div>
                <div className="space-y-4">
                  {departmentsLoading ? (
                    // Loading skeleton
                    Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Skeleton className="h-5 w-32" />
                          <Skeleton className="h-4 w-20" />
                        </div>
                        <Skeleton className="h-2 w-full" />
                      </div>
                    ))
                  ) : departmentStats.length === 0 ? (
                    // Empty state
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <Building2 className="h-10 w-10 text-muted-foreground/50 mb-3" />
                      <p className="text-sm text-muted-foreground">No departments yet</p>
                      <Button variant="link" size="sm" asChild className="mt-2">
                        <Link to="/settings/organization/departments">
                          Create your first department
                          <ArrowRight className="ml-1 h-3 w-3" />
                        </Link>
                      </Button>
                    </div>
                  ) : (
                    // Department list
                    departmentStats.map((dept) => (
                      <div key={dept.name} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{dept.name}</span>
                            <Badge variant="outline">{dept.count} employees</Badge>
                          </div>
                          <span className="text-sm text-muted-foreground">
                            {dept.percentage}% attendance
                          </span>
                        </div>
                        <Progress value={dept.percentage} />
                      </div>
                    ))
                  )}
                </div>
              </Card>
            )}

            {/* Recent Activity */}
            <Card
              className={
                canViewDepartments ? 'min-w-0 p-6 lg:col-span-3' : 'min-w-0 p-6 lg:col-span-7'
              }
            >
              <div className="mb-4">
                <h3 className="text-lg font-semibold">Recent Activity</h3>
                <p className="text-sm text-muted-foreground">Latest updates and changes</p>
              </div>
              <div className="space-y-4">
                {activityLoading ? (
                  // Loading skeleton
                  Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="flex items-start gap-3 rounded-lg border p-3">
                      <Skeleton className="h-8 w-8 rounded-full" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-3 w-1/4" />
                      </div>
                    </div>
                  ))
                ) : recentActivities.length === 0 ? (
                  // Empty state
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <Clock className="h-10 w-10 text-muted-foreground/50 mb-3" />
                    <p className="text-sm text-muted-foreground">No recent activity</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Activities will appear here as your team works
                    </p>
                  </div>
                ) : (
                  // Activity list
                  recentActivities.map((activity) => (
                    <div key={activity.id} className="flex items-start gap-3 rounded-lg border p-3">
                      <div className="rounded-full bg-primary/10 p-2">
                        {activity.type === 'EMPLOYEE_JOINED' && (
                          <UserCheck className="h-4 w-4 text-green-600" />
                        )}
                        {activity.type === 'LEAVE_APPROVED' && (
                          <Calendar className="h-4 w-4 text-green-600" />
                        )}
                        {activity.type === 'LEAVE_REQUESTED' && (
                          <Calendar className="h-4 w-4 text-yellow-600" />
                        )}
                        {activity.type === 'LEAVE_REJECTED' && (
                          <Calendar className="h-4 w-4 text-red-600" />
                        )}
                        {activity.type === 'PAYROLL_PROCESSED' && (
                          <DollarSign className="h-4 w-4 text-primary" />
                        )}
                        {activity.type === 'ATTENDANCE_MARKED' && (
                          <Clock className="h-4 w-4 text-primary" />
                        )}
                        {activity.type === 'EMPLOYEE_LEFT' && (
                          <UserX className="h-4 w-4 text-red-600" />
                        )}
                        {activity.type === 'DOCUMENT_UPLOADED' && (
                          <Upload className="h-4 w-4 text-primary" />
                        )}
                      </div>
                      <div className="flex-1 space-y-1">
                        <p className="text-sm font-medium">{activity.description}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatDateTime(activity.timestamp)}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </div>

          {/* Permission-Based Quick Actions */}
          <Card className="min-w-0 p-6">
            <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-lg font-semibold">Quick Actions</h3>
                <p className="text-sm text-muted-foreground">
                  Actions shown here match your current role and ABAC permissions
                </p>
              </div>
              {!quickActionsLoading && (
                <Badge variant="outline">{visibleQuickActions.length} available</Badge>
              )}
            </div>
            {quickActionsLoading ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 3 }).map((_, index) => (
                  <Skeleton key={index} className="h-20 w-full rounded-lg" />
                ))}
              </div>
            ) : visibleQuickActions.length === 0 ? (
              <div className="rounded-lg border border-dashed p-6 text-center">
                <Settings className="mx-auto mb-3 h-8 w-8 text-muted-foreground/60" />
                <p className="text-sm font-medium">No quick actions assigned</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Add permissions through roles, positions, departments, or teams to unlock actions.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {visibleQuickActions.map((action) => {
                  const Icon = QUICK_ACTION_ICONS[action.icon] ?? Settings;
                  return (
                    <Link
                      key={action.id}
                      to={action.href}
                      className="group flex items-center gap-3 rounded-lg border p-4 transition-colors hover:bg-muted/50"
                    >
                      <div className="rounded-md bg-primary/10 p-2">
                        <Icon className="h-4 w-4 text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium">{action.label}</p>
                        <p className="truncate text-xs text-muted-foreground">
                          {action.description}
                        </p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  );
                })}
              </div>
            )}
          </Card>

          {/* Employment status */}
          <Card className="min-w-0 p-4 sm:p-5">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-base font-semibold sm:text-lg">Employment Status</h3>
                <p className="text-sm text-muted-foreground">
                  Active, leave, and inactive workforce mix
                </p>
              </div>
              <Badge variant="outline" className="shrink-0">
                {stats.totalEmployees} total
              </Badge>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {employmentStatusItems.map(({ label, value, percent, Icon, tone, dot }) => (
                <div
                  key={label}
                  className="flex min-h-[76px] items-center justify-between gap-3 rounded-lg border bg-muted/20 p-3 md:flex-col md:items-start md:justify-between md:p-4"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <div className={`rounded-full p-2 ring-1 ${tone}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{label}</p>
                      <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                        <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
                        {percent}% of total
                      </div>
                    </div>
                  </div>
                  {statsLoading ? (
                    <Skeleton className="h-8 w-16 shrink-0" />
                  ) : (
                    <p className="shrink-0 text-2xl font-semibold tabular-nums">{value}</p>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
